import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { UseQueryResult } from 'react-query';
import { IEmptyStateView, IFitoListProps } from '~/Components';
import { TableDefinition } from '~/Components/DataTable/DataTable.types';
import { IDragHistoryItem, useBoolean, useDebouncedSearch } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IMenuAction, IMenuBulkAction } from '~/Models/interfaces/menuAction.models';
import {
  useFitoListDnD,
  useFitoListReactQueryPagination,
  useFitoListDesktop,
  useFitoListSelection,
  useFitoListFiltersTabsSearch,
  useFitoListGenerateQuery,
} from './FitoListReactQueryHooks';
import { IFitoList, IFitoListSettings, IFitoListTab } from './interfaces';
import { useInfiniteScroll } from './useInfiniteScroll';
import { isEqual } from 'lodash-es';

export interface IFitoListReactQueryProps<T> {
  keyOfId: keyof T;
  queryResults: (
    defaultQuery: { [name: string]: any },
    props?: any,
  ) => UseQueryResult<T[], IErrorResponse>;
  setItemValues: (item: T) => IFitoList;
  settings: IFitoListSettings;
  defaultQuery?: { [name: string]: any };
  title?: string;
  placeholder?: IEmptyStateView;
  tabs?: IFitoListTab[];
  actions?: (item: T) => IMenuAction[];
  bulkActions?: IMenuBulkAction[];
  onRearrange?: (historyItem: IDragHistoryItem[], extraData?: any) => void;
  pageSize?: number;
  onSelect?: (id: string | string[], checked?: boolean) => void;
  size?: 'small';
  tableDefinition?: TableDefinition;
  appliedFilters?: { [key: string]: any | undefined };
  filterComponent?: React.ReactNode;
  defaultSelectedIds?: string[];
  backendSearch?: (searchString: string) => UseQueryResult<T[], IErrorResponse>;
  searchTextPlaceholder?: string;
  infiniteScroll?: boolean;
  moreActions?: JSX.Element;
}

export const useFitoListReactQuery = <T extends object>({
  keyOfId,
  placeholder,
  appliedFilters,
  filterComponent,
  queryResults,
  tabs,
  setItemValues,
  actions,
  bulkActions,
  settings,
  onRearrange,
  pageSize = 10,
  onSelect,
  tableDefinition,
  defaultQuery = {},
  defaultSelectedIds,
  backendSearch,
  size,
  title,
  searchTextPlaceholder,
  infiniteScroll = false,
  moreActions,
}: IFitoListReactQueryProps<T>): IFitoListProps => {
  const hasNPlusOne = !!settings.paginationSettings?.hasNPlusOne;
  const NPlusOnePaginationWorkaround = settings?.paginationSettings?.hasNPlusOne ? 1 : 0;

  // state
  const [searchText, setSearchText] = useState('');
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isWaitingForRearrangeToEnd, setIsWaitingForRearrangeToEnd] = useState(false);
  const [accumulatedList, setAccumulatedList] = useState<T[]>([]);

  const searchResponse = backendSearch?.(searchText);

  // hooks
  const {
    state: forceRefresh,
    setTrue: enableForceRefresh,
    setFalse: unsetForceRefresh,
  } = useBoolean(true);

  const {
    onNextPage,
    onPreviousPage,
    page,
    paginationQuery,
    setPage,
  } = useFitoListReactQueryPagination({
    pageSize,
    settings,
    enableForceRefresh,
  });

  const { data, error, isLoading, query, responseHeaders } = useFitoListGenerateQuery({
    appliedFilters,
    defaultQuery,
    paginationQuery,
    queryResults,
    selectedTabIndex,
    setIsWaitingForRearrangeToEnd,
    tabs,
    enableForceRefresh,
    forceRefresh,
    unsetForceRefresh,
  });

  const xPagination = useMemo(() => JSON.parse(responseHeaders?.['x-pagination'] ?? '{}'), [
    responseHeaders?.['x-pagination'],
  ]);
  const total = responseHeaders?.['x-total-count'] || xPagination?.TotalCount;

  const isNextNavigationDisabled = accumulatedList.length >= total;

  useInfiniteScroll({
    callback: onNextPage,
    isLoading: isLoading || !!searchResponse?.isLoading,
    infiniteScroll,
    disabled: isNextNavigationDisabled,
  });

  const updateAccumulatedList = useCallback(() => {
    if (infiniteScroll && data && !isLoading) {
      setAccumulatedList((prevList) => {
        const firstPrevListElement = { ...prevList[0], status: undefined };
        const firstDataElement = { ...data[0], status: undefined };

        if (isEqual(firstPrevListElement, firstDataElement)) {
          return prevList;
        }

        return [...prevList, ...data];
      });
    }
  }, [data, infiniteScroll, isLoading]);

  useEffect(() => {
    updateAccumulatedList();
  }, [updateAccumulatedList]);

  const accumulatedListData: T[] = infiniteScroll ? accumulatedList : data || [];

  const unstructuredList = useMemo(
    () => (searchText ? searchResponse?.data : accumulatedListData) || [],
    [searchText, searchResponse?.data, accumulatedList, accumulatedListData],
  );

  const {
    addToDnDHistory,
    isBeingRearranged,
    onRearrangeStart,
    setIsBeingRearranged,
  } = useFitoListDnD({
    unstructuredList,
    onRearrange,
    enableForceRefresh,
    setIsWaitingForRearrangeToEnd,
  });

  const { setInputText } = useDebouncedSearch((text: string) => {
    setSearchText(text);
    setPage(settings?.paginationSettings?.startAt ?? 1);
  });

  const { Actions, BulkActions, OnSelect, selectedIds } = useFitoListSelection({
    query,
    bulkActions,
    defaultSelectedIds,
    onSelect,
  });

  const { filteredList, onTabbedFilterChange, tabbedFilters } = useFitoListFiltersTabsSearch({
    enableForceRefresh,
    isBeingRearranged,
    keyOfId,
    searchResponse,
    searchText,
    tabs,
    unstructuredList,
    settings,
    setPage,
    setItemValues,
    actions,
    setSelectedTabIndex,
  });

  const items = useMemo(() => {
    if (infiniteScroll) {
      return filteredList.map((item) => ({ ...item, checked: !!selectedIds.includes(item.id) }));
    }

    return filteredList
      .slice(0, pageSize + NPlusOnePaginationWorkaround)
      .map((item) => ({ ...item, checked: !!selectedIds.includes(item.id) }));
  }, [filteredList, pageSize, NPlusOnePaginationWorkaround, selectedIds]);

  const { desktopView } = useFitoListDesktop({ items, keyOfId, unstructuredList, tableDefinition });

  return {
    items,
    tabbedFilters,
    onTabbedFilterChange,
    onSearch: settings.search ? setInputText : undefined,
    Actions,
    bulkActions: BulkActions,
    isLoading:
      isLoading || forceRefresh || isWaitingForRearrangeToEnd || !!searchResponse?.isLoading,
    onSelect: OnSelect,

    addToDnDHistory,
    onRearrange: (settings.rearrange || undefined) && onRearrange && onRearrangeStart,
    isBeingRearranged,
    setIsBeingRearranged,

    error: error || searchResponse?.error || undefined,
    errorAction: enableForceRefresh,

    filterComponent,
    pageSize,
    page: infiniteScroll ? undefined : !settings.pagination || searchText ? undefined : page,
    hasNPlusOne,
    pageStartValue: settings.paginationSettings?.startAt ?? 1,
    onNextPage,
    onPreviousPage,
    multiSelect: !!settings.multiSelect,
    isSelectPossible: !!settings.select,
    isSelectionActive: !!selectedIds.length,
    placeholder,
    desktopView,
    size,
    title,
    total,
    searchTextPlaceholder,
    infiniteScroll,
    moreActions,
  };
};
